import { useTranslation } from "react-i18next";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Check, Crown, RefreshCw, Settings, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { trackBillingFunnel } from "@/lib/billing-funnel";
import { track } from "@/lib/analytics";
import { createCheckoutSession } from "@/utils/payments.functions";
import {
  canFallBackToWebCheckout,
  isStoreManagedBilling,
  mustUseStoreBilling,
  nativePlatform,
  storeLabel,
  storeProductId,
  PRODUCT_DISPLAY_NAMES,
} from "@/lib/store-billing";
import { StoreSubscriptionTerms } from "@/components/StoreSubscriptionTerms";
import { STUDY_TERMS } from "@/lib/study-policy";
import { QuestionCreditStore } from "@/components/QuestionCreditStore";
import {
  APPLE_DISCLOSURE,
  CANCEL_ANYTIME,
  NO_TRIAL_CTA,
  NO_TRIAL_DISCLOSURE,
  TRIAL_CANCEL_NOTE,
  TRIAL_CTA,
  TRIAL_DISCLOSURE,
  TRIAL_HEADLINE,
  TRIAL_SUBHEAD,
} from "@/lib/trial";
import { useTrialEligibility } from "@/hooks/useTrialEligibility";
import { useStorePrices } from "@/hooks/useStorePrices";
import { TrialConfirmDialog } from "@/components/TrialConfirmDialog";
import {
  purchasePremiumViaStore,
  restoreStorePurchases,
  openStoreManagement,
} from "@/lib/native-billing";

const TITLE = "Inquira Plans & Pricing — Free, Pro Monthly, Pro Yearly";
const DESC =
  "Compare Inquira Free Forever, Premium Monthly at $9.99/month, and the proposed $79.99 annual plan. New annual and question packs remain unavailable until verified.";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/pricing" }],
  }),
});

const PRO_FEATURES = [
  STUDY_TERMS.premiumBenefit,
  "Homework image scanning",
  "Priority AI processing",
  "Deeper explanations",
  "Practice mode",
  "Step-by-step solutions",
  "Image & text input",
  "Solution history",
  "Handwriting recognition",
] as const;

const FREE_FEATURES = [
  "3 AI tutor questions per day under the new Free terms",
  "One complimentary homework image scan for a new account",
  "Limited quizzes and flashcards under the new Free terms",
  "Step-by-step solutions",
  "Image & text input",
  "Solution history",
] as const;

function FeatureList({ items }: { items: readonly string[] }) {
  return (
    <ul className="space-y-2 text-sm text-ink-muted">
      {items.map((f) => (
        <li key={f} className="flex items-start gap-2">
          <Check className="mt-0.5 size-4 shrink-0 text-neon-pink" aria-hidden="true" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

function PricingPage() {
  const { t } = useTranslation();
  useEffect(() => {
    trackBillingFunnel("billing_view");
  }, []);
  const { user, isPaid, planPriceId, premium, refreshPremium, refreshUsage } = useAuth();
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const [busy, setBusy] = useState<null | "purchase" | "restore" | "manage">(null);
  const [confirmingTrial, setConfirmingTrial] = useState(false);
  const native = mustUseStoreBilling();
  const storeManaged = isStoreManagedBilling();
  // Apple StoreKit decides introductory-offer eligibility inside the native app.
  const { eligible: trialEligible, loading: eligibilityLoading } = useTrialEligibility(premium);
  // Apple/Google report the localized price for the customer's storefront.
  const storePrices = useStorePrices();
  const monthlyPrice = storePrices.monthly ?? "$9.99";
  const yearlyPrice = "$79.99";

  const planLabel = planPriceId === "inquira_pro_yearly" ? "Pro Yearly" : "Pro Monthly";

  const upgrade = async (plan: "monthly" | "yearly") => {
    if (plan === "yearly") { toast.info(t("monetization.the_new_annual_plan_is_not_available_yet")); return; }
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setBusy("purchase");
    trackBillingFunnel("checkout_start");
    try {
      if (native) {
        // Apple / Google require in-app purchases inside the native wrapper.
        void track("checkout_started", { surface: "pricing_native", product_id: storeProductId(plan) });
        const result = await purchasePremiumViaStore(plan);
        if (!result.ok) {
          if (result.error === "cancelled") return;
          // Wrapper without a billing bridge: keep a payable path everywhere
          // except iOS, where Apple forbids external checkout.
          if (
            (result.error === "no_bridge" || result.error === "store_not_configured") &&
            canFallBackToWebCheckout()
          ) {
            void track("checkout_fallback_web", { surface: "pricing_native", reason: result.error });
            const fallbackPriceId = "inquira_pro_monthly";
            const fallback = await createCheckoutSession({
              data: { priceId: fallbackPriceId, origin: window.location.origin },
            });
            if (fallback?.url) {
              window.location.href = fallback.url;
              return;
            }
            toast.error("Couldn't start checkout. Please try again.");
            return;
          }
          toast.error(
            result.error === "pending"
              ? "Your purchase is pending approval. Premium unlocks as soon as it's approved."
              : result.error === "already_owned"
                ? "That subscription is already active — tap Restore Purchases."
                : result.error === "product_unavailable" || result.error === "no_bridge"
                  ? `We couldn't load ${PRODUCT_DISPLAY_NAMES[plan]} from the ${storeLabel(nativePlatform())}. Please try again.`
                  : "We couldn't complete that purchase. Please try again.",
          );
          return;
        }
        trackBillingFunnel("purchase_complete");
        await refreshPremium();
        toast.success(t("monetization.premium_is_active_your_confirmed_study_benefits_are_available"));
        return;
      }
      const priceId = "inquira_pro_monthly";
      void track("checkout_started", { surface: "pricing_web", price_id: priceId });
      const result = await createCheckoutSession({ data: { priceId, origin: window.location.origin } });
      if (!result?.url) {
        toast.error("Couldn't start checkout. Please try again.");
        return;
      }
      window.location.href = result.url;
    } catch (e) {
      console.error("Pricing upgrade failed:", e);
      toast.error("Couldn't start checkout. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const restore = async () => {
    setBusy("restore");
    try {
      const result = await restoreStorePurchases();
      await refreshPremium();
      await refreshUsage();
      if (result.premium) toast.success("Purchases restored — Premium is active.");
      else toast.info("No active subscription was found for this account.");
    } catch {
      toast.error("Couldn't restore purchases. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy("manage");
    try {
      const opened = await openStoreManagement();
      if (!opened) {
        window.location.href = "/subscription";
      }
    } finally {
      setBusy(null);
    }
  };

  const yearly = billing === "yearly";

  return (
    <div className="min-h-screen notebook-lines">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:py-12">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> Back to Inquira
        </Link>

        <header className="mb-8 text-center">
          <h1 className="font-[family-name:var(--font-display)] text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            Choose Your Inquira Plan
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-ink-muted sm:text-base">
            Start free. Choose Premium when you want more study support.
          </p>
        </header>

        <div
          role="tablist"
          aria-label="Billing period"
          className="mx-auto mb-8 flex w-fit items-center gap-1 rounded-full border border-rule bg-card p-1"
        >
          {(["monthly", "yearly"] as const).map((p) => (
            <button
              key={p}
              role="tab"
              aria-selected={billing === p}
              onClick={() => setBilling(p)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                billing === p ? "bg-neon-pink text-white" : "text-ink-muted hover:text-ink"
              }`}
            >
              {p === "monthly" ? "Monthly" : "Yearly"}
            </button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {/* Free */}
          <section className="flex flex-col rounded-2xl border border-rule bg-card p-5">
            <h2 className="font-[family-name:var(--font-display)] text-xl font-extrabold text-ink">Free</h2>
            <p className="mt-1 text-3xl font-extrabold text-ink">$0</p>
            <p className="mb-4 text-xs text-ink-muted">Forever — no card required</p>
            <div className="flex-1">
              <FeatureList items={FREE_FEATURES} />
            </div>
            <div className="mt-5">
              {user ? (
                <Button variant="subtle" size="full" disabled={!isPaid}>
                  {isPaid ? "Included with Pro" : "Current Plan"}
                </Button>
              ) : (
                <Link to="/login" className="block">
                  <Button variant="subtle" size="full">Create a free account</Button>
                </Link>
              )}
            </div>
          </section>

          {/* Pro Monthly */}
          <section
            className={`relative flex flex-col rounded-2xl border bg-card p-5 ${
              !yearly ? "border-neon-pink shadow-lg" : "border-rule"
            }`}
          >
            <span className="absolute -top-3 left-5 rounded-full bg-neon-pink px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white">
              Most popular
            </span>
            <h2 className="font-[family-name:var(--font-display)] text-xl font-extrabold text-ink">Pro Monthly</h2>
            <p className="mt-1 text-3xl font-extrabold text-ink">
              {monthlyPrice}<span className="text-base font-semibold text-ink-muted">/month</span>
            </p>
            <p className="text-xs text-ink-muted">Billed monthly, renews automatically</p>
            {trialEligible ? (
              <div className="mb-4 mt-2 rounded-xl bg-neon-yellow/20 p-3 text-sm font-bold text-ink">
                <p className="text-base">{TRIAL_HEADLINE}</p>
                <p>{TRIAL_SUBHEAD}</p>
                <p>{CANCEL_ANYTIME}</p>
                <p className="mt-1 text-xs font-semibold text-ink">{TRIAL_CANCEL_NOTE}</p>
              </div>
            ) : (
              <div className="mb-4 mt-2 rounded-xl bg-muted/60 p-3 text-sm font-bold text-ink">
                <p>{monthlyPrice}/month</p>
                <p>{CANCEL_ANYTIME}</p>
              </div>
            )}
            <div className="flex-1">
              <FeatureList items={PRO_FEATURES} />
            </div>
            <div className="mt-5 space-y-2">
              {isPaid ? (
                <Button variant="subtle" size="full" disabled>
                  <Crown className="size-4" /> {planLabel === "Pro Monthly" ? "Current Plan" : "You're on Pro"}
                </Button>
              ) : eligibilityLoading ? (
                <Button variant="scan" size="full" disabled>
                  <Sparkles className="size-4" /> Loading plan…
                </Button>
              ) : trialEligible ? (
                <>
                  <Button
                    variant="scan"
                    size="full"
                    onClick={() => setConfirmingTrial(true)}
                    disabled={busy !== null}
                  >
                    <Sparkles className="size-4" /> {TRIAL_CTA}
                  </Button>
                  <p className="text-xs font-semibold text-ink">{TRIAL_DISCLOSURE}</p>
                </>
              ) : (
                <>
                  <Button
                    variant="scan"
                    size="full"
                    onClick={() => upgrade("monthly")}
                    disabled={busy !== null}
                  >
                    <Sparkles className="size-4" /> {NO_TRIAL_CTA}
                  </Button>
                  <p className="text-xs font-semibold text-ink">{NO_TRIAL_DISCLOSURE}</p>
                </>
              )}
            </div>
          </section>

          {/* Pro Yearly */}
          <section
            className={`relative flex flex-col rounded-2xl border bg-card p-5 ${
              yearly ? "border-neon-pink shadow-lg" : "border-rule"
            }`}
          >
            <span className="absolute -top-3 left-5 rounded-full bg-neon-yellow px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-black">
              Best value
            </span>
            <h2 className="font-[family-name:var(--font-display)] text-xl font-extrabold text-ink">Pro Yearly</h2>
            <p className="mt-1 text-3xl font-extrabold text-ink">
              {yearlyPrice}<span className="text-base font-semibold text-ink-muted">/year</span>
            </p>
            <p className="text-xs text-ink-muted">{t("monetization.proposed_price_not_available_for_purchase_yet")}</p>
            <p className="mb-4 text-xs font-semibold text-neon-pink">{t("monetization.save_approximately_33_compared_with_paying_monthly")}</p>
            <p className="mb-4 text-sm font-bold text-ink">{CANCEL_ANYTIME}</p>
            <div className="flex-1">
              <FeatureList items={PRO_FEATURES} />
            </div>
            <div className="mt-5">
              {isPaid ? (
                <Button variant="subtle" size="full" disabled>
                  <Crown className="size-4" /> {planLabel === "Pro Yearly" ? "Current Plan" : "You're on Pro"}
                </Button>
              ) : (
                <Button
                  variant="scan"
                  size="full"
                  onClick={() => upgrade("yearly")}
                  disabled
                >
                  <Sparkles className="size-4" /> {t("monetization.annual_plan_coming_soon")}
                </Button>
              )}
            </div>
          </section>
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {storeManaged && (
            <Button variant="subtle" onClick={restore} disabled={busy !== null}>
              <RefreshCw className="size-4" /> {busy === "restore" ? "Restoring…" : "Restore Purchases"}
            </Button>
          )}
          {isPaid && (
            <Button variant="subtle" onClick={manage} disabled={busy !== null}>
              <Settings className="size-4" /> Manage or Cancel Subscription
            </Button>
          )}
          <Link to="/subscription">
            <Button variant="subtle">
              <Settings className="size-4" /> Your subscription
            </Button>
          </Link>
        </div>

        <div className="mx-auto mt-6 max-w-xl">
          <StoreSubscriptionTerms plan="monthly" price={storePrices.monthly} fallbackPrice="$9.99" />
          {storePrices.error && (
            <p className="mt-2 text-center text-xs font-semibold text-neon-pink">{storePrices.error}</p>
          )}
        </div>

        <section className="mt-8 rounded-2xl border border-rule bg-card p-5 text-xs leading-relaxed text-ink-muted">
          <h2 className="mb-2 text-sm font-bold text-ink">Subscription details</h2>
          <p>
            Premium Monthly is $9.99 per month and renews automatically until canceled. The proposed
            Premium Annual plan is $79.99 per year and is not yet available. The new daily limits are staged,
            not active; existing benefits remain unchanged pending review and verified billing setup.
          </p>
          <p className="mt-2">
            The 3-day free trial applies to Inquira Premium Monthly and requires a valid payment method.
            When the trial ends, the subscription automatically renews at $9.99/month unless canceled before
            the trial ends. Canceling before the trial ends avoids the first $9.99 charge.
          </p>
          <p className="mt-2">
            You can cancel anytime. Canceling stops all future renewals; access generally continues through
            the subscription period you already paid for, according to the billing platform's rules. In the
            iOS app, the 3-day free trial is an App Store introductory offer and Apple determines whether
            your Apple Account is eligible for it.
          </p>
          <p className="mt-2">
            For purchases made in the Inquira iOS app: {APPLE_DISCLOSURE}
          </p>
          <p className="mt-3 flex flex-wrap gap-3">
            <Link to="/terms" className="font-semibold text-neon-pink underline">Terms of Service</Link>
            <Link to="/privacy" className="font-semibold text-neon-pink underline">Privacy Policy</Link>
            <Link to="/support" className="font-semibold text-neon-pink underline">Help & Support</Link>
          </p>
        </section>
        {user && <QuestionCreditStore />}
      </div>
      {confirmingTrial && (
        <TrialConfirmDialog
          surface="pricing"
          onClose={() => setConfirmingTrial(false)}
          onActivated={async () => {
            await refreshPremium();
            await refreshUsage();
          }}
        />
      )}
    </div>
  );
}